import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { faArrowDown, faArrowUp, faBarsSort, faCheck } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { SearchField } from "@/components/SearchField/SearchField";

import { Order } from "@/types/types";
import { classNames } from "@/utils/classNames";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import {
	DEFAULT_SORT_KEY,
	DEFAULT_SORT_ORDER,
	MOST_USED_SORTS,
	SortByDropdownProps,
	SortOption,
} from "./sort-by-dropdown.types";

const PANEL_WIDTH = 280;

/**
 * SortByDropdown — the L3 toolbar "Sort by" control (PREC-3544).
 *
 * Renders a portal panel with a search field, a curated "Most used" group, a divider, and the
 * currently **visible** sortable columns alphabetically. Selecting an option drives the same sort
 * mechanism the column headers use ({@link SortParams}); the parent passes the active
 * `sortBy`/`sortOrder` and an `onSort` callback (e.g. the list slice's `setInboxSorting`).
 *
 * Direction toggle mirrors the column header: picking a new field sorts ASC; re-picking the active
 * field flips ASC↔DESC.
 *
 * Portal target is `portalContainer ?? document.body` so the menu never clips inside the toolbar's
 * overflow container (web rule §11).
 */
export const SortByDropdown: FunctionComponent<SortByDropdownProps> = ({
	sortBy,
	sortOrder,
	onSort,
	columnOptions = [],
	extraMostUsed = [],
	portalContainer,
	testId,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`inbox.${key}`), [t]);

	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const anchorRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

	const mostUsed: SortOption[] = useMemo(
		() => [
			...MOST_USED_SORTS.map(({ key, orderTitle }) => ({
				orderTitle,
				title: ts(`sortByOptions.${key}`),
			})),
			...extraMostUsed,
		],
		[ts, extraMostUsed]
	);

	// Alphabetical list of the currently visible sortable columns, de-duplicated by orderTitle.
	const alphabeticalColumns: SortOption[] = useMemo(() => {
		const seen = new Set<string>();
		const unique = columnOptions.filter((option) => {
			if (!option.orderTitle || seen.has(option.orderTitle)) {
				return false;
			}

			seen.add(option.orderTitle);

			return true;
		});

		return unique.sort((a, b) => a.title.localeCompare(b.title));
	}, [columnOptions]);

	const matchesSearch = useCallback(
		(option: SortOption) => option.title.toLowerCase().includes(search.trim().toLowerCase()),
		[search]
	);

	// The "Default" entry restores each surface's natural ordering — on Outbox/Inbox that's the
	// schedule/paused/replies-aware smart order, on the List it's recency. Selecting it clears the
	// URL sort params (see useSortUrlSync). Active whenever no explicit sort is set.
	const defaultOption: SortOption = useMemo(
		() => ({ orderTitle: DEFAULT_SORT_KEY, title: ts("sortByDefault") }),
		[ts]
	);
	const isDefaultActive = sortBy === DEFAULT_SORT_KEY;
	const showDefaultOption = matchesSearch(defaultOption);

	const filteredMostUsed = useMemo(() => mostUsed.filter(matchesSearch), [mostUsed, matchesSearch]);
	const filteredColumns = useMemo(
		() => alphabeticalColumns.filter(matchesSearch),
		[alphabeticalColumns, matchesSearch]
	);

	const updatePosition = useCallback(() => {
		if (!anchorRef.current) {
			return;
		}

		const rect = anchorRef.current.getBoundingClientRect();
		// Right-align the panel to the trigger (the control sits at the right of the toolbar), but
		// clamp into the viewport so it never spills off the left edge.
		const left = Math.max(8, rect.right - PANEL_WIDTH);

		setPosition({ top: rect.bottom + 4, left });
	}, []);

	useEffect(() => {
		if (!open) {
			return;
		}

		updatePosition();
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [open, updatePosition]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const handleOutside = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			const insideAnchor = anchorRef.current?.contains(target as Node);
			const insidePanel = panelRef.current?.contains(target as Node);

			if (!insideAnchor && !insidePanel) {
				setOpen(false);
			}
		};

		window.addEventListener("click", handleOutside);

		return () => window.removeEventListener("click", handleOutside);
	}, [open]);

	const toggleOpen = (event: React.MouseEvent) => {
		event.stopPropagation();
		setOpen((prev) => {
			const next = !prev;

			if (next) {
				setSearch("");
			}

			return next;
		});
	};

	const handlePick = (option: SortOption) => {
		const isActive = option.orderTitle === sortBy;
		const nextOrder = isActive && sortOrder === Order.ASC ? Order.DESC : Order.ASC;

		onSort({ sortBy: option.orderTitle, sortOrder: nextOrder });
		setOpen(false);
	};

	const selectDefault = () => {
		onSort({ sortBy: DEFAULT_SORT_KEY, sortOrder: DEFAULT_SORT_ORDER });
		setOpen(false);
	};

	const renderOption = (option: SortOption) => {
		const isActive = option.orderTitle === sortBy;

		return (
			<button
				key={option.orderTitle}
				className={classNames(
					"flex w-full cursor-pointer flex-row items-center justify-between px-3 py-2 text-left text-sm duration-200 hover:bg-gray-100",
					isActive ? "text-brand-light font-medium" : "text-gray-900"
				)}
				type="button"
				onClick={(event) => {
					event.stopPropagation();
					handlePick(option);
				}}
			>
				<span className="truncate">{option.title}</span>
				{isActive && (
					<Icon
						className="ml-2 h-3 w-3 flex-shrink-0 text-brand-light"
						icon={sortOrder === Order.ASC ? faArrowUp : faArrowDown}
					/>
				)}
			</button>
		);
	};

	const hasResults = showDefaultOption || filteredMostUsed.length > 0 || filteredColumns.length > 0;

	return (
		<div ref={anchorRef} className="relative w-fit" data-test={testId}>
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				image={<Icon className="mr-2" icon={faBarsSort} />}
				size={ButtonSize.S}
				title={ts("sortBy")}
				onClick={toggleOpen}
			/>

			{open &&
				ReactDOM.createPortal(
					<div
						ref={panelRef}
						className="fixed z-[2000] overflow-hidden rounded-md border border-gray-200 bg-white shadow-md"
						style={
							position
								? { top: position.top, left: position.left, width: PANEL_WIDTH }
								: { visibility: "hidden" }
						}
						onClick={(event) => event.stopPropagation()}
					>
						<div className="border-b border-border-light p-2">
							<SearchField
								handleChange={(event) => setSearch(event.target.value)}
								handleReset={() => setSearch("")}
								name="sort-by-search"
								placeholder={ts("sortBySearchPlaceholder")}
								testId="sort-by-search"
								value={search}
							/>
						</div>

						<div className="max-h-[320px] overflow-y-auto bb-scrollbar-darker py-1">
							{showDefaultOption && (
								<>
									<button
										className={classNames(
											"flex w-full cursor-pointer flex-row items-center justify-between px-3 py-2 text-left text-sm duration-200 hover:bg-gray-100",
											isDefaultActive ? "text-brand-light font-medium" : "text-gray-900"
										)}
										type="button"
										onClick={(event) => {
											event.stopPropagation();
											selectDefault();
										}}
									>
										<span className="truncate">{defaultOption.title}</span>
										{isDefaultActive && (
											<Icon
												className="ml-2 h-3 w-3 flex-shrink-0 text-brand-light"
												icon={faCheck}
											/>
										)}
									</button>
									{(filteredMostUsed.length > 0 || filteredColumns.length > 0) && (
										<div className="my-1 h-px bg-border-light" />
									)}
								</>
							)}

							{filteredMostUsed.length > 0 && (
								<>
									<div className="px-3 py-1 text-xxs font-semibold uppercase tracking-wide text-gray-500">
										{ts("sortByMostUsed")}
									</div>
									{filteredMostUsed.map(renderOption)}
								</>
							)}

							{filteredMostUsed.length > 0 && filteredColumns.length > 0 && (
								<div className="my-1 h-px bg-border-light" />
							)}

							{filteredColumns.map(renderOption)}

							{!hasResults && (
								<p className="px-3 py-2.5 text-sm italic text-gray-700">{t("basics.noResults")}</p>
							)}
						</div>
					</div>,
					portalContainer ?? document.body
				)}
		</div>
	);
};
